import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, HeartHandshake, Lock, ArrowRight } from "lucide-react";
import { storage } from "@/lib/api";
import { QUIZ_PROGRESS_KEY, LEAD_SUBMITTED_KEY } from "@/lib/quizConfig";
import { trackCustomOnce } from "@/lib/pixel";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_connexus-quiz-flow/artifacts/inn9zrlw_Logo.png";

const QUIZ_LANDING_VIEW_KEY = "meta_quiz_landing_view_fired";

export default function Landing() {
  const navigate = useNavigate();

  // Fire a custom QuizLandingView event once per browser session,
  // independent of Meta's default PageView (which is left to fire as standard).
  useEffect(() => {
    try {
      if (sessionStorage.getItem(QUIZ_LANDING_VIEW_KEY) === "1") return;
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("trackCustom", "QuizLandingView");
      }
      sessionStorage.setItem(QUIZ_LANDING_VIEW_KEY, "1");
    } catch {
      /* noop */
    }
  }, []);

  const startQuiz = () => {
    storage.remove(QUIZ_PROGRESS_KEY);
    storage.remove(LEAD_SUBMITTED_KEY);
    trackCustomOnce("StartQuiz");
    navigate("/quiz");
  };

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-4xl mx-auto px-6 pb-20 fade-up">
        {/* Hero */}
        <section className="pt-8 md:pt-12 text-center md:text-left max-w-2xl mx-auto md:mx-0">
          <p
            className="text-[11px] md:text-xs tracking-[0.25em] font-semibold text-connexus mb-4"
            data-testid="brand-label"
          >
            THE COUPLES CONNECTION QUIZ
          </p>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-connexus mb-5"
            data-testid="primary-headline"
          >
            Your Relationship Has a Blind Spot.
          </h1>

          {/* Welcome video — left-aligned within hero column */}
          <div
            className="w-full aspect-video rounded-2xl overflow-hidden bg-black shadow-connexus-card mb-7"
            data-testid="welcome-video-section"
          >
            <iframe
              src="https://www.youtube-nocookie.com/embed/Btu-SSPOOao?rel=0&modestbranding=1&playsinline=1"
              title="A Personal Welcome From Wouter"
              loading="lazy"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              className="w-full h-full border-0"
              data-testid="welcome-video-iframe"
            />
          </div>

          <p
            className="text-lg text-connexus-textSecondary leading-relaxed mb-7 max-w-xl mx-auto md:mx-0"
            data-testid="sub-headline"
          >
            This 3-minute quiz reveals what&apos;s missing (and how to fix it).
          </p>

          <div className="flex flex-wrap gap-5 md:gap-7 justify-center md:justify-start mb-8 text-sm text-connexus-textSecondary">
            <span className="inline-flex items-center gap-2">
              <Clock className="w-4 h-4 text-connexus" /> 3 min
            </span>
            <span className="inline-flex items-center gap-2">
              <HeartHandshake className="w-4 h-4 text-connexus" /> 19 questions
            </span>
            <span className="inline-flex items-center gap-2">
              <Lock className="w-4 h-4 text-connexus" /> Private &amp; free
            </span>
          </div>

          <button
            onClick={startQuiz}
            className="inline-flex items-center justify-center gap-3 bg-connexus hover:bg-connexus-hover text-white px-8 py-4 rounded-full text-base font-semibold shadow-connexus-soft transition-all active:scale-[0.98]"
            data-testid="start-quiz-button"
          >
            Start the Quiz
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="mt-3 text-xs text-connexus-textMuted">
            Personalized results.
          </p>
        </section>

        {/* Trust strip */}
        <div className="mt-12 py-3 px-4 bg-connexus-accent text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-wide text-connexus">
            Built by Love and Abundance Coaches Avani and Wouter Shahlenis
          </p>
        </div>

        {/* How it works */}
        <section className="mt-16">
          <p className="text-[11px] tracking-[0.25em] font-semibold text-connexus mb-3">
            HOW IT WORKS
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-black mb-8 max-w-xl">
            Three simple steps towards a Relationship that feels alive again.
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                n: "1",
                title: "Answer 19 questions",
                body: "One screen at a time. Honest answers only—no wrong ones.",
              },
              {
                n: "2",
                title: "Get your results",
                body: "See which area needs the most love right now, and why.",
              },
              {
                n: "3",
                title: "Reconnect tonight",
                body: "Two simple practices to start closing the gap—tonight.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="bg-connexus-accent rounded-2xl p-6"
                data-testid={`how-step-${s.n}`}
              >
                <div className="w-9 h-9 rounded-full bg-connexus text-white flex items-center justify-center font-semibold mb-3">
                  {s.n}
                </div>
                <h3 className="font-semibold text-base text-black mb-1">
                  {s.title}
                </h3>
                <p className="text-sm text-connexus-textSecondary leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        <footer className="mt-20 pt-8 text-center">
          <p className="text-xs text-connexus-textMuted">
            © {new Date().getFullYear()} Connexus — the key to love, intimacy &amp; magic.
          </p>
        </footer>
      </main>
    </div>
  );
}
